import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=user
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setToken(data.access_token);
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);       // includes resolved `permissions`
      } catch {
        setUser({
          id: data.id, email: data.email, name: data.name,
          role: data.role, status: data.status,
        });
      }
      return { ok: true };
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setToken(null);
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, error, login, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function hasMinRole(user, min) {
  const rank = {
    staff: 1, finance_officer: 1, hr_officer: 1, procurement_officer: 1,
    compliance_officer: 2, asset_manager: 2,
    finance_manager: 2, hr_manager: 2, procurement_manager: 2, project_manager: 2,
    manager: 2,
    management: 3,
    admin: 4, ceo: 5, super_admin: 6,
  };
  return (rank[user?.role] || 0) >= (rank[min] || 0);
}
